/**
 * UsuarioTemplate — template CRUD da entidade Usuário.
 *
 * Declara o model (esquema de campos), renderers, menus de ação e formulário
 * específicos de Usuário. Renderiza o componente genérico EntityGrid com essas
 * configurações, expondo uma tela completa de listagem e manutenção de usuários.
 *
 * Para criar um template de outra entidade, consulte o JSDoc de
 * src/components/grid/EntityGrid.js (seção "Como criar uma nova entidade").
 */
import React, { Component } from 'react';
import { TabPanel } from 'primereact/tabview';
import { Avatar } from 'primereact/avatar';
import { confirmDialog } from 'primereact/confirmdialog';

import { TabViewComErro } from 'hay-core-react';
import { EntityGrid } from 'hay-core-react';
import { ModelSenha } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelCalendar } from 'hay-core-react';
import { ModelInputBytes } from 'hay-core-react';
import { ModelTempo } from 'hay-core-react';
import { ModelBoolean } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import { ApiService } from 'hay-core-react';
import { Usuario } from 'hay-core-react';
import Plano from '../../../models/Plano';
import Voucher from '../../../models/Voucher';
import UsuarioGenero from '../../../enums/UsuarioGenero';
import UsuarioClassificacao from '../../../enums/UsuarioClassificacao';
import UsuarioProvedorEnum from '../../../enums/UsuarioProvedorEnum';
import PlanoEstiloNavegacaoEnum from '../../../enums/PlanoEstiloNavegacaoEnum';
import { buildRelacionados } from '../../../service/RotasService';

// ─── Esquema de campos ────────────────────────────────────────────────────────

const model = {
    nome: {
        exibir: 'main.usuario.nome',
        visivel: true,
        largura: '1rem',
        icone: 'pi-user',
        validacao: {
            obrigatorio: true,
            tamanhominimo: 3,
            minimopalavras: 2
        },
    },
    telefonemovel: {
        exibir: 'main.usuario.telefonemovel',
        visivel: true,
        keyfilter: "num",
        icone: 'pi-phone',
        validacao: {
            tamanhominimo: 8,
            telefonecomddd: true
        },
        largura: "20rem",
        fixar: true,
    },
    email: {
        exibir: 'main.usuario.email',
        keyfilter: "email",
        visivel: true,
        icone: 'pi-at',
        validacao: {
            tamanhominimo: 3,
            email: true
        },
        largura: "20rem",
        fixar: true,
    },
    imagem: {
        exibir: 'main.usuario.imagem',
        visivel: true,
        largura: "5rem",
        fixar: true,
        template: 'imagem.template'
    },
    ultimoAcesso: {
        exibir: 'main.usuario.ultimoAcesso',
        largura: '10rem',
        tipo: 'date',
        time: true
    },
    genero: {
        exibir: 'main.usuario.genero',
        tipo: 'enum',
        entidade: "genero.entidade",
    },
    classificacao: {
        exibir: 'main.usuario.classificacao',
        tipo: 'enum',
        entidade: "classificacao.entidade",
        validacao: { obrigatorio: true }
    },
    pass: {
        exibir: 'main.usuario.pass',
        validacao: {
            identico: { campo: 'passConf', descricao: "main.usuario.passConf" },
            tamanhominimo: 6,
        },
        hidegrid: true,
    },
    passConf: {
        exibir: 'main.usuario.passConf',
        validacao: {
            identico: { campo: 'pass', descricao: "main.usuario.pass" },
            tamanhominimo: 6,
        },
        hidegrid: true,
    },
    cpf: {
        exibir: 'main.usuario.cpf',
        keyfilter: "num",
        icon: 'pi-id-card',
        validacao: {
            cpf: true,
            tamanhominimo: 11,
            tamanhomaximo: 14
        },
    },
    cidade: {
        exibir: 'main.usuario.cidade',
        icone: 'pi-map-marker',
    },
    datanascimento: {
        exibir: 'main.usuario.datanascimento',
        tipo: 'date',
    },
    dataaceitetermo: {
        exibir: 'main.usuario.dataaceitetermo',
        tipo: 'date',
        time: true,
    },
    dataconfirmacaocadastro: {
        exibir: 'main.usuario.dataconfirmacaocadastro',
        tipo: 'date',
        time: true,
    },
    termouso: {
        exibir: 'main.usuario.termouso',
        tipo: 'relacao',
        camporelacao: 'versao',
    },
    ultimoprovedor: {
        exibir: 'main.usuario.ultimoprovedor',
        tipo: 'enum',
        entidade: 'ultimoprovedor.entidade',
    },
    ultimocontratointegracao: {
        exibir: 'main.usuario.ultimocontratointegracao',
        icone: 'pi-briefcase',
    },
    plano: {
        exibir:       'main.usuario.plano',
        tipo:         'relacao',
        camporelacao: 'descricao',
        entidade:     'plano.entidade',
        template:     'plano.template',
    },
    voucher: {
        exibir:       'main.usuario.voucher',
        tipo:         'relacao',
        camporelacao: 'codigo',
        entidade:     'voucher.entidade',
        template:     'voucher.template',
    },
    estilonavegacao: {
        exibir:   'main.usuario.estilonavegacao',
        tipo:     'enum',
        entidade: 'estilonavegacao.entidade',
    },
    temponavegacao: {
        exibir:   'main.usuario.temponavegacao',
        tipo:     'numeric',
        template: 'temponavegacao.template',
    },
    bytesnavegacao: { exibir: 'main.usuario.bytesnavegacao', tipo: 'bytes' },
    tempovalidoate: {
        exibir: 'main.usuario.tempovalidoate',
        tipo: 'date',
        time: true,
    },
    bytesnavegacaodown: { exibir: 'main.usuario.bytesnavegacaodown', tipo: 'bytes' },
    bytesnavegacaoup:   { exibir: 'main.usuario.bytesnavegacaoup',   tipo: 'bytes' },
    tempoconsumido: {
        exibir:   'main.usuario.tempoconsumido',
        tipo:     'numeric',
        template: 'tempoconsumido.template',
    },
    atingiulimitenavegacao: { exibir: 'main.usuario.atingiulimitenavegacao', tipo: 'boolean' },
    updatedAt: {
        exibir: "main.updatedAt",
        largura: '10rem',
        tipo: 'date',
        time: true
    },
    createdAt: {
        exibir: "main.createdAt",
        largura: '10rem',
        tipo: 'date',
        time: true
    },
};

// ─── Renderers customizados ───────────────────────────────────────────────────

const renderers = {
    imagem: {
        template: (usuario) => (
            <div className="flex justify-content-center">
                {usuario.imagem
                    ? <img src={usuario.imagem} alt="foto" className="w-2rem h-2rem shadow-2 border-circle" style={{ objectFit: 'cover' }} />
                    : <Avatar icon="pi pi-user" shape="circle" className="shadow-2" />}
            </div>
        )
    },
    genero:        { entidade: UsuarioGenero },
    classificacao: { entidade: UsuarioClassificacao },
    'ultimoprovedor.entidade': UsuarioProvedorEnum,
    'estilonavegacao.entidade': PlanoEstiloNavegacaoEnum,
    plano:   { entidade: Plano,   template: (rowData) => rowData.plano?.descricao || '' },
    voucher: { entidade: Voucher, template: (rowData) => rowData.voucher?.codigo || '' },
    temponavegacao: { template: (rowData) => (rowData.temponavegacao > 0 ? UtilService.formatarDuracaoCurta(rowData.temponavegacao) : '—') },
    tempoconsumido: { template: (rowData) => (rowData.tempoconsumido > 0 ? UtilService.formatarDuracaoCurta(rowData.tempoconsumido) : '—') },
};

// ─── Formulário de campos (render prop) ───────────────────────────────────────

class UsuarioFormulario extends Component {
    render() {
        const { model, contexto } = this.props;
        const isHotspot = model.classificacao.value?.id === 'main.usuario.classificacao.hotspot';
        return (
            <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
                <TabPanel header={UtilService.traduzir('main.usuario.aba.basica')} leftIcon="pi pi-user mr-2">
                    <div className="grid">
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.nome} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.telefonemovel} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.email} contexto={contexto} onBlur={() => contexto.updateModelPath('email.value', (model.email.value || '').toLowerCase().trim())} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.cpf} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.cidade} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelCalendar model={model.datanascimento} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelSenha model={model.pass} contexto={contexto} mostrarMetricas={true} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelSenha model={model.passConf} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelDropDown model={model.genero} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelDropDown model={model.classificacao} contexto={contexto} />
                        </div>
                    </div>
                </TabPanel>
                {isHotspot && (
                    <TabPanel header={UtilService.traduzir('main.usuario.aba.informacoesadicionais')} leftIcon="pi pi-file-check mr-2">
                        <div className="grid">
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelDropDown model={model.ultimoprovedor} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelTextInput model={model.ultimocontratointegracao} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelCalendar model={model.dataaceitetermo} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelCalendar model={model.dataconfirmacaocadastro} contexto={contexto} />
                            </div>
                            <div className="field col-12 mt-3">
                                <span className="text-sm text-color-secondary block mb-2">{contexto.traduzir('main.usuario.termouso.texto')}</span>
                                <div className="surface-100 border-round p-3 text-sm" style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflowY: 'auto' }}>
                                    {model.termouso.value?.texto || '-'}
                                </div>
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <span className="text-sm text-color-secondary block mb-2">{contexto.traduzir('main.usuario.termouso.versao')}</span>
                                <span>{model.termouso.value?.versao ?? '-'}</span>
                            </div>
                        </div>
                    </TabPanel>
                )}
                {isHotspot && (
                    <TabPanel header={UtilService.traduzir('main.usuario.aba.navegacao')} leftIcon="pi pi-wifi mr-2">
                        <div className="grid">
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelDropDown model={model.plano} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelDropDown model={model.voucher} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelDropDown model={{ ...model.estilonavegacao, disabled: true }} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelTempo model={model.temponavegacao} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelInputBytes model={model.bytesnavegacao} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelCalendar model={model.tempovalidoate} contexto={contexto} />
                            </div>

                            <div className="field col-12 md:col-6 mt-3">
                                <ModelInputBytes model={model.bytesnavegacaodown} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelInputBytes model={model.bytesnavegacaoup} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelTempo model={{ ...model.tempoconsumido, disabled: true }} contexto={contexto} />
                            </div>
                            <div className="field col-12 md:col-6 mt-3">
                                <ModelBoolean model={{ ...model.atingiulimitenavegacao, disabled: true }} contexto={contexto} />
                            </div>
                        </div>
                    </TabPanel>
                )}
            </TabViewComErro>
        );
    }
}

// ─── Template (componente exportado) ─────────────────────────────────────────

class UsuarioTemplate extends Component {

    processos() {
        return [{
            label: 'main.duplicar',
            icon: 'pi pi-clone',
            disabled: true,
            ativo: (processo, selected) => {
                processo.disabled = selected.length !== 1;
                return processo;
            },
            command: () => { this.gridRef?.duplicar(); }
        }, {
            label: 'main.usuario.forcarnovasenha',
            icon: 'pi pi-key',
            disabled: true,
            ativo: (processo, selected) => {
                processo.disabled = selected.length !== 1 || selected[0].classificacao !== 'main.usuario.classificacao.sistema';
                return processo;
            },
            command: () => {
                const usuario = this.gridRef?.getSelected()?.[0];
                if (!usuario) return;
                confirmDialog({
                    header: UtilService.traduzir('main.usuario.forcarnovasenha.confirmacao'),
                    message: UtilService.traduzir('main.usuario.forcarnovasenha.confirmacao.mensagem'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: async () => {
                        const resultado = await ApiService.post('usuario/forcarnovasenha', { id: usuario.id });
                        if (resultado?.status) {
                            UtilService.showToast({ severity: 'success', summary: UtilService.traduzir('main.usuario.forcarnovasenha.sucesso') });
                            await this.gridRef?.atualizar();
                        } else {
                            UtilService.showToast({ sticky: true, severity: 'error', summary: UtilService.traduzir('main.usuario.forcarnovasenha.erro'), detail: UtilService.traduzir(resultado?.data?.state) || resultado?.data?.message });
                        }
                    },
                });
            },
        }];
    }

    render() {
        return (
            <EntityGrid
                ref={(ref) => this.gridRef = ref}
                entidade={Usuario}
                model={model}
                identidade="main.usuario"
                permissoes={this.props.permissoes}
                icone="pi-users"
                storageKey="dt-usuarios"
                processos={this.processos()}
                getRelacionados={(selected) => buildRelacionados(
                    'usuario',
                    this.props.permissoes,
                    this.props.navigate,
                    () => selected?.[0],
                )}
                renderers={renderers}
                configuracao={this.props.configuracao}
                navigate={this.props.navigate}
                location={this.props.location}
                searchparams={this.props.searchparams}
                formulario={(m, ctx) => <UsuarioFormulario model={m} contexto={ctx} />}
            />
        );
    }
}

export default UsuarioTemplate;
